<template>
  <b-modal
    :visible="value"
    title="Conciliação Financeira (ECONF)"
    size="xl"
    scrollable
    :no-close-on-backdrop="ocupado"
    :no-close-on-esc="ocupado"
    :hide-header-close="ocupado"
    @change="$emit('input', $event)"
    @hidden="$emit('input', false)"
  >
    <div v-if="nota" class="econf-nota mb-4">
      <strong
        >{{
          Number(nota.modelo || String(nota.chave).slice(20, 22)) === 65
            ? "NFC-e"
            : "NF-e"
        }}
        {{ nota.numeroNota || nota.numero }}</strong
      >
      <span class="ml-3">{{ nota.nomeCliente }}</span>
      <span class="d-inline-block ml-3 font-weight-bold text-primary"
        >Valor da nota: {{ moeda(nota.valorTotal) }}</span
      >
      <div class="text-muted text-break mt-1">{{ nota.chave }}</div>
    </div>
    <b-alert :show="!!erro" variant="danger" role="alert">{{ erro }}</b-alert>
    <b-alert
      :show="!!resultado"
      :variant="(resultado && resultado.variant) || 'info'"
      role="status"
      >{{ resultado && resultado.mensagem }}</b-alert
    >
    <div v-if="carregando" class="text-center p-5">
      <b-spinner label="Carregando ECONF" />
    </div>
    <template v-else>
      <b-button v-if="falhaInicial" variant="outline-primary" @click="carregar"
        >Tentar novamente</b-button
      >
      <div v-else>
        <section
          class="econf-secao econf-secao-registro"
          aria-labelledby="econf-registro-titulo"
        >
          <h5 id="econf-registro-titulo" class="mb-4">Origem do recebimento</h5>
          <b-alert :show="!habilitado" variant="info"
            >O ECONF não está habilitado e vigente para {{ uf }}. Verifique a
            habilitação e a vigência em Configurações fiscais → Conciliação
            Financeira (ECONF).</b-alert
          >
          <b-alert :show="Number(nota && nota.status) !== 2" variant="warning"
            >Esta nota está cancelada. Você pode consultar o histórico e
            cancelar eventos existentes.</b-alert
          >
          <b-form @submit.prevent="registrar(false)">
            <fieldset :disabled="ocupado || !podeRegistrar">
              <EconfPagamentoForm
                v-for="(pagamento, indice) in pagamentos"
                :key="indice"
                :value="pagamento"
                :indice="indice"
                :removivel="pagamentos.length > 1"
                :ocupado="ocupado || !podeRegistrar"
                @input="$set(pagamentos, indice, $event)"
                @remover="pagamentos.splice(indice, 1)"
              />
              <div class="d-flex flex-wrap econf-botoes">
                <b-button
                  type="submit"
                  variant="outline-primary"
                  :disabled="ocupado || !podeRegistrar"
                  >Salvar evento pendente</b-button
                >
                <b-button
                  variant="primary"
                  :disabled="ocupado || !podeRegistrar"
                  @click="registrar(true)"
                  ><b-spinner v-if="ocupado" small class="mr-2" />Registrar e
                  transmitir à SEFAZ</b-button
                >
              </div>
            </fieldset>
          </b-form>
        </section>
        <section
          class="econf-secao econf-secao-eventos"
          aria-labelledby="econf-eventos-titulo"
        >
          <h5 id="econf-eventos-titulo" class="mb-2">Eventos registrados</h5>
          <p class="text-muted mb-4">
            Consulte os eventos já registrados e acompanhe os retornos da SEFAZ.
          </p>
          <div
            class="d-flex flex-wrap justify-content-between align-items-center my-4"
          >
            <span>{{ eventosTotal }} evento(s) registrado(s) no sistema</span>
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="ocupado || carregandoEventos"
              @click="carregarEventos(paginaEventos)"
              >Atualizar eventos</b-button
            >
          </div>
          <b-table
            :items="eventos"
            :fields="camposEventos"
            :busy="carregandoEventos"
            responsive
            show-empty
            empty-text="Nenhum evento ECONF registrado para esta nota."
            small
          >
            <template #cell(tipo_evento)="{ item }"
              >{{
                Number(item.tipo_evento) === 110751
                  ? "Cancelamento"
                  : "Conciliação"
              }}
              <div class="text-muted">
                Sequência {{ item.sequencia }}
              </div></template
            >
            <template #cell(status)="{ item }"
              ><b-badge :variant="status(item.status)[1]">{{
                status(item.status)[0]
              }}</b-badge>
              <div v-if="item.cstat" class="text-muted">
                SEFAZ: {{ item.cstat }}
              </div>
              <div
                v-if="retornosConsulta[item.id]"
                class="econf-retorno-consulta mt-2 p-2 rounded"
                :class="'alert-' + retornosConsulta[item.id].variant"
                role="status"
                aria-live="polite"
              >
                <b-spinner
                  v-if="retornosConsulta[item.id].carregando"
                  small
                  class="mr-1"
                />
                {{ retornosConsulta[item.id].mensagem }}
              </div></template
            >
            <template #cell(valor)="{ item }">{{
              Number(item.tipo_evento) === 110751
                ? "—"
                : moeda(
                    (item.pagamentos || []).reduce(
                      (total, p) => total + Number(p.vPag),
                      0
                    )
                  )
            }}</template>
            <template #cell(protocolo)="{ item }"
              ><span class="text-break">{{
                item.protocolo || "—"
              }}</span></template
            >
            <template #cell(acoes)="{ item }">
              <div class="d-flex flex-wrap econf-botoes">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="ocupado"
                  @click="detalhes(item)"
                  >Detalhes</b-button
                >
                <b-button
                  v-if="podeTransmitir(item)"
                  size="sm"
                  variant="primary"
                  :disabled="ocupado"
                  @click="executarAcao('transmitir', item)"
                  >{{
                    item.status === "pendente" ? "Transmitir" : "Reenviar"
                  }}</b-button
                >
                <b-button
                  v-if="!['pendente', 'descartado'].includes(item.status)"
                  size="sm"
                  variant="outline-info"
                  :disabled="ocupado"
                  @click="executarAcao('consultar', item)"
                  >Consultar SEFAZ</b-button
                >
                <b-button
                  v-if="
                    Number(item.tipo_evento) === 110750 &&
                    item.status === 'registrado'
                  "
                  size="sm"
                  variant="outline-danger"
                  :disabled="ocupado"
                  @click="executarAcao('cancelar', item)"
                  >Cancelar evento</b-button
                >
                <b-button
                  v-if="
                    Number(item.tipo_evento) === 110750 &&
                    ['pendente', 'erro', 'rejeitado'].includes(item.status)
                  "
                  size="sm"
                  variant="outline-secondary"
                  :disabled="ocupado"
                  @click="executarAcao('descartar', item)"
                  >Descartar</b-button
                >
                <b-button
                  v-if="['registrado', 'cancelado'].includes(item.status)"
                  size="sm"
                  variant="outline-success"
                  :disabled="ocupado"
                  @click="baixarXml(item, 'processado')"
                  >XML</b-button
                >
              </div>
            </template>
          </b-table>
          <b-pagination
            v-if="eventosTotal > porPagina"
            v-model="paginaEventos"
            :total-rows="eventosTotal"
            :per-page="porPagina"
            :disabled="ocupado || carregandoEventos"
            align="center"
            @input="carregarEventos"
            aria-label="Páginas de eventos ECONF"
          />
          <b-card
            v-if="eventoDetalhe"
            class="my-4"
            :title="'Evento ' + eventoDetalhe.id"
          >
            <div class="d-flex justify-content-between">
              <p>
                <strong>{{ status(eventoDetalhe.status)[0] }}</strong> ·
                {{
                  Number(eventoDetalhe.ambiente) === 1
                    ? "Produção"
                    : "Homologação"
                }}
              </p>
              <b-button size="sm" variant="link" @click="eventoDetalhe = null"
                >Fechar detalhes</b-button
              >
            </div>
            <p v-if="eventoDetalhe.motivo" class="text-break">
              {{ eventoDetalhe.motivo }}
            </p>
            <p>
              Protocolo:
              {{ eventoDetalhe.protocolo || "Ainda não confirmado" }} · Data:
              {{
                dataFormatada(
                  eventoDetalhe.registrado_em || eventoDetalhe.data_evento
                )
              }}
            </p>
            <p v-if="eventoDetalhe.id_evento_original">
              Cancelamento do evento {{ eventoDetalhe.id_evento_original }}
            </p>
            <div
              v-for="(pagamento, indice) in eventoDetalhe.pagamentos"
              :key="indice"
              class="border-top pt-3 mb-3"
            >
              <strong
                >{{ meioPagamento(pagamento.tPag) }} ·
                {{ moeda(pagamento.vPag) }} ·
                {{ dataFormatada(pagamento.dPag) }}</strong
              >
              <div v-if="pagamento.xPag">{{ pagamento.xPag }}</div>
              <div v-if="pagamento.cAut">Autorização: {{ pagamento.cAut }}</div>
              <div v-if="pagamento.tBand">Bandeira: {{ pagamento.tBand }}</div>
              <div v-if="pagamento.CNPJPag">
                Processado em: {{ pagamento.CNPJPag }} / {{ pagamento.UFPag }}
              </div>
              <div v-if="pagamento.CNPJIF">
                Instituição financeira: {{ pagamento.CNPJIF }}
              </div>
              <div v-if="pagamento.CNPJReceb">
                Beneficiário: {{ pagamento.CNPJReceb }} /
                {{ pagamento.UFReceb }}
              </div>
            </div>
            <div class="econf-botoes">
              <b-button
                size="sm"
                variant="outline-secondary"
                :disabled="ocupado"
                @click="baixarXml(eventoDetalhe, 'envio')"
                >XML enviado</b-button
              ><b-button
                size="sm"
                variant="outline-secondary"
                :disabled="ocupado"
                @click="baixarXml(eventoDetalhe, 'retorno')"
                >XML de retorno</b-button
              >
            </div>
            <h6 class="mt-4">Histórico de tentativas</h6>
            <ul class="pl-4">
              <li
                v-for="tentativa in eventoDetalhe.historico"
                :key="tentativa.id"
              >
                {{
                  tentativa.operacao === "consulta" ? "Consulta" : "Transmissão"
                }}
                · {{ dataFormatada(tentativa.created_at)
                }}<span v-if="tentativa.erro"> · {{ tentativa.erro }}</span>
              </li>
            </ul>
          </b-card>
        </section>
      </div>
    </template>
    <template #modal-footer
      ><b-button :disabled="ocupado" @click="$emit('input', false)"
        >Fechar</b-button
      ></template
    >
  </b-modal>
</template>

<script>
import { saveAs } from "file-saver";
import EconfPagamentoForm from "./EconfPagamentoForm.vue";
import {
  STATUS,
  MEIOS_PAGAMENTO,
  ufDaNota,
  configuracaoPadrao,
  novaReferencia,
  novoPagamento,
  limparPagamento,
  validarPagamentos,
  erroEconf,
  moeda,
  dataFormatada,
  hoje,
} from "@/modules/NFe/services/econf";

export default {
  name: "EconfModal",
  components: { EconfPagamentoForm },
  props: { value: Boolean, nota: { type: Object, default: null } },
  data() {
    return {
      carregando: false,
      falhaInicial: false,
      ocupado: false,
      erro: "",
      resultado: null,
      retornosConsulta: {},
      configuracao: configuracaoPadrao(),
      eventos: [],
      eventosTotal: 0,
      paginaEventos: 1,
      porPagina: 25,
      carregandoEventos: false,
      pagamentos: [novoPagamento(this.nota)],
      referencia: novaReferencia(),
      eventoDetalhe: null,
      versao: 0,
      eventosRequisicao: 0,
      camposEventos: [
        { key: "tipo_evento", label: "Evento" },
        { key: "status", label: "Situação" },
        { key: "valor", label: "Valor" },
        { key: "protocolo", label: "Protocolo" },
        { key: "acoes", label: "Ações" },
      ],
    };
  },
  computed: {
    uf() {
      return ufDaNota(this.nota);
    },
    habilitado() {
      return (
        !!this.configuracao.habilitado &&
        this.configuracao.inicio_vigencia <= hoje()
      );
    },
    podeRegistrar() {
      return this.habilitado && Number(this.nota?.status) === 2;
    },
    empresaId() {
      return (
        this.$store?.getters?.empresaContexto?.id_empresa ||
        this.$store?.getters?.currentUser?.usuario_empresa?.id_empresa ||
        null
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(aberto) {
        if (aberto) this.carregar();
        else this.versao++;
      },
    },
    empresaId(atual, anterior) {
      if (atual !== anterior && this.value) {
        this.versao++;
        this.$emit("input", false);
      }
    },
  },
  beforeDestroy() {
    this.versao++;
  },
  methods: {
    moeda,
    dataFormatada,
    novoPagamento() {
      return novoPagamento(this.nota);
    },
    status(valor) {
      return STATUS[valor] || [valor, "secondary"];
    },
    meioPagamento(codigo) {
      return (
        MEIOS_PAGAMENTO.find((item) => item.value === codigo)?.text || codigo
      );
    },
    async carregar() {
      const versao = ++this.versao;
      this.carregando = true;
      this.falhaInicial = false;
      this.erro = "";
      this.resultado = null;
      this.eventos = [];
      this.retornosConsulta = {};
      this.eventoDetalhe = null;
      this.paginaEventos = 1;
      this.pagamentos = [novoPagamento(this.nota)];
      this.referencia = novaReferencia();
      try {
        const resposta = await this.$http.get("/nfe/econf/configuracoes");
        if (versao !== this.versao) return;
        this.configuracao =
          resposta.data.find((item) => item.uf === this.uf) ||
          configuracaoPadrao(this.uf);
        const carregamentos = await Promise.all([this.carregarEventos(1)]);
        if (versao === this.versao && carregamentos.includes(false))
          this.falhaInicial = true;
      } catch (erro) {
        if (versao === this.versao) {
          this.erro = erroEconf(erro);
          this.falhaInicial = true;
        }
      } finally {
        if (versao === this.versao) this.carregando = false;
      }
    },
    async carregarEventos(pagina = 1, eventoConsultaId = null) {
      const versao = this.versao;
      const requisicao = ++this.eventosRequisicao;
      this.carregandoEventos = true;
      try {
        const { data } = await this.$http.get("/nfe/econf/eventos", {
          params: {
            id_nfe: this.nota.id,
            por_pagina: this.porPagina,
            page: pagina,
          },
        });
        if (versao !== this.versao || requisicao !== this.eventosRequisicao)
          return;
        this.eventos = data.data;
        this.eventosTotal = data.total;
        this.paginaEventos = data.current_page;
        return true;
      } catch (erro) {
        if (versao === this.versao) {
          if (eventoConsultaId) {
            const anterior = this.retornosConsulta[eventoConsultaId];
            this.$set(this.retornosConsulta, eventoConsultaId, {
              variant: "warning",
              mensagem:
                (anterior?.mensagem || "") +
                " Não foi possível atualizar a lista: " +
                erroEconf(erro),
            });
          } else this.erro = erroEconf(erro);
        }
        return false;
      } finally {
        if (versao === this.versao && requisicao === this.eventosRequisicao)
          this.carregandoEventos = false;
      }
    },
    async registrar(transmitir) {
      if (this.ocupado || !this.podeRegistrar) return;
      this.erro = "";
      this.resultado = null;
      this.erro = validarPagamentos(this.pagamentos);
      if (this.erro) return;
      const dados = { id_nfe: this.nota.id, referencia: this.referencia };
      dados.pagamentos = this.pagamentos.map((pagamento) =>
        limparPagamento(pagamento)
      );
      const versao = this.versao;
      this.ocupado = true;
      let eventoCriado = null;
      try {
        const { data } = await this.$http.post("/nfe/econf/eventos", dados);
        eventoCriado = data;
        if (versao !== this.versao) return;
        this.pagamentos = [novoPagamento(this.nota)];
        this.referencia = novaReferencia();
        this.resultado = {
          variant: "success",
          mensagem:
            "Evento pendente salvo. A transmissão pode ser feita pelas ações do evento.",
        };
        if (transmitir) {
          const resposta = await this.$http.post(
            `/nfe/econf/eventos/${data.id}/transmitir`
          );
          if (versao !== this.versao) return;
          this.notificar(resposta.data);
        }
      } catch (erro) {
        if (versao === this.versao)
          this.erro =
            erroEconf(erro) +
            (eventoCriado
              ? " O evento foi salvo; consulte sua situação antes de registrar outro pagamento."
              : " Atualize os eventos ou repita esta operação para verificar se o registro foi recebido.");
      } finally {
        if (versao === this.versao) {
          await Promise.all([this.carregarEventos(1)]);
          this.ocupado = false;
          this.$emit("atualizado");
        }
      }
    },
    podeTransmitir(evento) {
      if (["pendente", "erro", "rejeitado", "incerto"].includes(evento.status))
        return true;
      return (
        evento.status === "enviando" &&
        Date.now() - Date.parse(evento.updated_at) > 5 * 60 * 1000
      );
    },
    async executarAcao(acao, evento) {
      if (this.ocupado || !this.value) return;
      const versao = this.versao;
      if (["cancelar", "descartar"].includes(acao)) {
        const resposta = await this.$swal.fire({
          icon: "warning",
          title:
            acao === "cancelar"
              ? "Cancelar este ECONF na SEFAZ?"
              : "Descartar o evento pendente?",
          text:
            acao === "cancelar"
              ? "O cancelamento do evento não estorna o recebimento financeiro."
              : "O recebimento financeiro será mantido.",
          showCancelButton: true,
          confirmButtonText: "Confirmar",
          cancelButtonText: "Voltar",
        });
        if (!resposta.isConfirmed && resposta.value !== true) return;
      }
      if (versao !== this.versao || !this.value) return;
      this.ocupado = true;
      this.erro = "";
      this.resultado = null;
      if (acao === "consultar")
        this.$set(this.retornosConsulta, evento.id, {
          variant: "info",
          mensagem: "Consultando a SEFAZ...",
          carregando: true,
        });
      try {
        const { data } = await this.$http.post(
          `/nfe/econf/eventos/${evento.id}/${acao}`
        );
        if (versao !== this.versao) return;
        if (acao === "consultar") {
          const variant = ["registrado", "cancelado"].includes(data.status)
            ? "success"
            : ["rejeitado", "erro"].includes(data.status)
            ? "danger"
            : "warning";
          this.$set(this.retornosConsulta, evento.id, {
            variant,
            mensagem: `${this.status(data.status)[0]}${
              data.cstat ? " (SEFAZ " + data.cstat + ")" : ""
            }: ${data.motivo || "Consulta concluída."}`,
          });
        } else this.notificar(data);
        this.eventoDetalhe = null;
      } catch (erro) {
        if (versao === this.versao) {
          if (acao === "consultar")
            this.$set(this.retornosConsulta, evento.id, {
              variant: "danger",
              mensagem: erroEconf(erro),
            });
          else this.erro = erroEconf(erro);
        }
      } finally {
        if (versao === this.versao) {
          await this.carregarEventos(
            this.paginaEventos,
            acao === "consultar" ? evento.id : null
          );
          this.ocupado = false;
          this.$emit("atualizado");
        }
      }
    },
    notificar(evento) {
      const confirmado = ["registrado", "cancelado", "descartado"].includes(
        evento.status
      );
      this.resultado = {
        variant: confirmado ? "success" : "warning",
        mensagem: confirmado
          ? Number(evento.tipo_evento) === 110751
            ? "Cancelamento registrado na SEFAZ."
            : `Evento ${this.status(evento.status)[0].toLowerCase()}.`
          : `${this.status(evento.status)[0]}: ${
              evento.motivo ||
              "Consulte a situação antes de registrar outro evento."
            }`,
      };
    },
    async detalhes(evento) {
      this.ocupado = true;
      this.erro = "";
      const versao = this.versao;
      try {
        const { data } = await this.$http.get(
          `/nfe/econf/eventos/${evento.id}`
        );
        if (versao === this.versao) this.eventoDetalhe = data;
      } catch (erro) {
        if (versao === this.versao) this.erro = erroEconf(erro);
      } finally {
        if (versao === this.versao) this.ocupado = false;
      }
    },
    async baixarXml(evento, tipo) {
      this.ocupado = true;
      this.erro = "";
      try {
        const { data } = await this.$http.get(
          `/nfe/econf/eventos/${evento.id}/xml`,
          { params: { tipo }, responseType: "blob" }
        );
        saveAs(data, `econf-${evento.id}-${tipo}.xml`);
      } catch (erro) {
        this.erro = erroEconf(erro, "O XML ainda não está disponível.");
      } finally {
        this.ocupado = false;
      }
    },
  },
};
</script>

<style scoped>
.econf-retorno-consulta {
  min-width: 180px;
  max-width: 320px;
  white-space: normal;
  overflow-wrap: anywhere;
  font-size: 0.9em;
}

.econf-secao {
  padding: 22px;
  border: 1px solid #dfe5ed;
  border-radius: 8px;
  background: #fff;
}
.econf-secao + .econf-secao {
  margin-top: 32px;
}
.econf-secao-registro {
  background: #f6faff;
  border-top: 3px solid var(--primary, #3699ff);
}
@media (max-width: 575px) {
  .econf-secao {
    padding: 16px;
  }
}

.econf-nota {
  background: #f5f8fa;
  border-radius: 6px;
  padding: 14px;
}
.econf-botoes {
  gap: 6px;
}
.econf-botoes .btn {
  margin-bottom: 4px;
}
</style>
